import { Div, ToolTip } from '@driftt/tide-core'
import qs from 'qs'
import { isEmpty, pathOr } from 'ramda'
import React from 'react'

import BotVariantIndicator from 'components/BotVariantIndicator'
import { META_TAGS } from 'constants/clpSettings'
import { userHasPlaybookReports } from 'utils/access'
import { formatTime } from 'utils/moment'
import { isMigratedPlaybook } from 'utils/playbooks'
import { trackClickedOnSettings } from 'utils/tracking'

const CUTOVER_TO_TEST_TAB = 1556812800000 // "Test" tab instead of "Landing page" tab for playbooks created after 5/2/2019 @ 12PM
const DRIFT_LINK_ONLY_TOOLTIP = (
  <Div>
    {' '}
    Targeting is disabled because
    <br /> this playbook is set to fire via #Driftlink only{' '}
  </Div>
)
const CLP_ONLY_TOOLTIP = (
  <Div>
    {' '}
    Targeting is disabled because
    <br /> this playbook is set to fire via landing page only{' '}
  </Div>
)

function generateLandingPageTab(root) {
  return {
    label: 'Landing page',
    route: `${root}/targeting/bot-page`,
    onClick: ({ history }) => history.push(`${root}/targeting/bot-page`),
  }
}

function generateEmbedNav(root, { hasExperiment }) {
  return [
    {
      label: <>Flow {hasExperiment ? <BotVariantIndicator /> : 'builder'}</>,
      route: `${root}/visual-builder`,
      onClick: ({ history }) => history.push(`${root}/visual-builder`),
    },
    ...(hasExperiment
      ? [
          {
            label: (
              <>
                Flow <BotVariantIndicator flowB />
              </>
            ),
            route: `${root}/visual-builder/experiment`,
            onClick: ({ history }) =>
              history.push(`${root}/visual-builder/experiment`),
          },
        ]
      : []),
    {
      label: 'Styles',
      route: `${root}/clp/styles`,
      onClick: ({ history }) => history.push(`${root}/clp/styles`),
    },
    {
      label: 'Content',
      route: `${root}/clp/content`,
      onClick: ({ history }) => history.push(`${root}/clp/content`),
    },
    {
      label: 'Settings',
      route: `${root}/settings`,
      onClick: ({ history }) => history.push(`${root}/settings`),
      handleClick: trackClickedOnSettings,
    },
    {
      label: 'Share',
      route: `${root}/clp/share`,
      onClick: ({ history }) => history.push(`${root}/clp/share`),
    },
  ]
}

export default function generateNavItems(props) {
  const {
    isDriftLinkOnly,
    isEnterprise,
    selectedPlaybookVersion,
    draftPlaybook,
    playbook,
    versionTimestamps,
    isFastlane,
    isFastlaneQuickSetup,
    isMeetingBooking,
  } = props

  const toolTipComponent = isDriftLinkOnly
    ? DRIFT_LINK_ONLY_TOOLTIP
    : CLP_ONLY_TOOLTIP

  const activePlaybookVersion = selectedPlaybookVersion ?? draftPlaybook

  const createdAt = pathOr(null, ['createdAt'], playbook)
  const playbookId = pathOr(null, ['id'], playbook)
  const isEmbeddedOnly = pathOr(
    false,
    ['meta', META_TAGS.EMBEDDED_ONLY_PLAYBOOK],
    playbook
  )
  const isMigrated = isMigratedPlaybook(playbook.reportType)
  const hasExperiment = !!activePlaybookVersion.experiment

  const ROOT = `/editor/${playbookId}`
  const embedNav = generateEmbedNav(ROOT, { hasExperiment, ...props })
  const landingPageNav = generateLandingPageTab(ROOT)

  function prepViewReportLink() {
    const type = isFastlane ? 'fastlane' : 'chat'
    const tab = hasExperiment ? 'experiment' : 'performance'
    const query = {}

    if (!isEmpty(versionTimestamps)) {
      const { createdTimestamp, retiredTimestamp } = versionTimestamps
      query.dateType = 'custom'
      query.startDate = formatTime(createdTimestamp, 'YYYY-MM-DD')
      query.endDate = formatTime(retiredTimestamp, 'YYYY-MM-DD')
    }

    window.open(
      `/reports/playbooks/${type}/${playbookId}/${tab}?${qs.stringify(query)}`,
      '_blank'
    )
  }

  const showLandingPageTab = !isMigrated && createdAt <= CUTOVER_TO_TEST_TAB
  const reportNavItem = {
    label: 'View report',
    route: '',
    onClick: prepViewReportLink,
    external: true,
  }

  const showViewReport =
    userHasPlaybookReports() && !isEnterprise && !isMeetingBooking

  if (isEmbeddedOnly) {
    if (showViewReport) {
      embedNav.push(reportNavItem)
    }

    return embedNav
  } else {
    const navItems = [
      {
        label: 'Install',
        route: `${ROOT}/install`,
        onClick: ({ history }) => history.push(`${ROOT}/install`),
        isHidden: () => !isFastlane || isFastlaneQuickSetup,
      },
      {
        label: isFastlaneQuickSetup ? 'Form setup & mapping' : 'Form mapping',
        route: `${ROOT}/form-setup`,
        onClick: ({ history }) => history.push(`${ROOT}/form-setup`),
        isHidden: () => !isFastlane,
      },
      {
        label: (
          <ToolTip
            className={`playbook-editor-nav--audience ${
              isDriftLinkOnly ? 'disabled' : ''
            }`}
            hover={isDriftLinkOnly}
            flow="right"
            component={toolTipComponent}
          >
            Targeting
          </ToolTip>
        ),
        disable: true,
        route: `${ROOT}/targeting/builder`,
        onClick: ({ history }) =>
          !isDriftLinkOnly && history.push(`${ROOT}/targeting/builder`),
      },
      {
        label: 'Bot message',
        route: `${ROOT}/bot-message`,
        onClick: ({ history }) => history.push(`${ROOT}/bot-message`),
        isHidden: () => !isMigrated,
      },
      {
        label: <>Bot flow {hasExperiment ? <BotVariantIndicator /> : ''}</>,
        route: `${ROOT}/visual-builder`,
        onClick: ({ history }) => history.push(`${ROOT}/visual-builder`),
        isHidden: () => isMigrated,
      },
      ...(hasExperiment
        ? [
            {
              label: (
                <>
                  Bot flow <BotVariantIndicator flowB />
                </>
              ),
              route: `${ROOT}/visual-builder/experiment`,
              onClick: ({ history }) =>
                history.push(`${ROOT}/visual-builder/experiment`),
            },
          ]
        : []),
      ...(showLandingPageTab ? [landingPageNav] : []),
      {
        label: 'Settings',
        route: `${ROOT}/settings`,
        onClick: ({ history }) => history.push(`${ROOT}/settings`),
        handleClick: trackClickedOnSettings,
      },
      {
        label: 'Driftlink',
        route: `${ROOT}/targeting/drift-link`,
        onClick: ({ history }) => history.push(`${ROOT}/targeting/drift-link`),
        isHidden: () => isFastlane || isMigrated,
      },
    ]

    if (showViewReport) {
      navItems.push(reportNavItem)
    }

    return navItems
  }
}
